import React, { useEffect, useState, useCallback } from 'react';
import { Modal, Collapse, Tag, Button, Row } from 'antd';
import { formatTime } from '../../../lib/util';
import { getTrainingStatus } from '../../../lib/util/status';
import { getTrainingInferenceList } from '../../../lib/api/training-api';
import { InferenceList } from './InferenceList';
import moment from 'moment';
import { Training as TrainingType, Inference, Model } from '../../../types';
import BatchUploader from '../../../common/BatchUploader';
import { useHistory } from 'react-router';
import { ModalTitle } from '../../../components/text/Titles';
import { Icon } from '@ant-design/compatible';
import { ErrorText } from '../../../components/text/ErrorText';

const { Panel } = Collapse;

moment.locale('nl');

type Props = {
  inProgress?: boolean;
  isOpen: boolean;
  training: TrainingType;
  index: number;
  model: Model;
  onItemClickUrl: (trainingId: string) => any;
};

export const Training = ({ training, onItemClickUrl, inProgress, isOpen, model, index }: Props) => {
  let history = useHistory();

  const [inferenceList, setInferenceList] = useState<Inference[]>([]);
  const [testModelVisible, setTestModelVisible] = useState<boolean>(false);
  const [error, setError] = useState<boolean>(false);

  const onBatchUploaded = () => {
    setTestModelVisible(false);
    history.push(`/demo/${model.algorithm.id}/models`);
  };

  const fetchInferenceList = useCallback(async () => {
    try {
      const inferenceList = await getTrainingInferenceList(training.id);
      setInferenceList(inferenceList);
      setError(false);
    } catch (e) {
      setError(true);
      setInferenceList([]);
    }
  }, [setInferenceList, training]);

  useEffect(() => {
    fetchInferenceList();

    return () => {
      Modal.destroyAll();
    };
  }, [fetchInferenceList]);

  const renderHeader = () => {
    return (
      <>
        {(training || inProgress) && (
          <Row className="w-full flex-row items-center justify-between">
            <Row className="items-center">
              <span className="text-black text-base">{training.modelVersionName}</span>
              <span className="text-gray-500 ml-3 text-sm">{formatTime(training.timeCreated)}</span>
            </Row>
            {training && (
              <Row className="items-center">
                {index === 0 && (
                  <Button
                    className="mr-4"
                    disabled={training.status === 'TRAINING'}
                    onClick={e => {
                      e.stopPropagation();
                      setTestModelVisible(true);
                    }}>
                    Test Model
                  </Button>
                )}
                <Tag color={getTrainingStatus(training).color} className="text-sm mr-0">
                  {training.status === 'TRAINING' && (
                    <Icon type="loading" className="mr-2" style={{ color: 'orange' }} />
                  )}
                  {getTrainingStatus(training).label}
                </Tag>
              </Row>
            )}
          </Row>
        )}
      </>
    );
  };

  return (
    <>
      {testModelVisible && (
        <Modal
          title={
            <ModalTitle>
              Upload to {model.name} {training.modelVersionName}
            </ModalTitle>
          }
          onCancel={() => setTestModelVisible(false)}
          visible={testModelVisible}
          footer={
            <Button type="default" onClick={() => setTestModelVisible(false)}>
              Cancel
            </Button>
          }>
          <BatchUploader
            multiple={true}
            modelId={null}
            modelVersion={training.resultModelVersionId || null}
            onBatchUploaded={() => {
              onBatchUploaded();
            }}
          />
        </Modal>
      )}
      <Collapse bordered={false} defaultActiveKey={isOpen ? ['1'] : []}>
        <Panel header={renderHeader()} key="1" style={{ borderColor: '#e8e8e8', backgroundColor: '#fff' }}>
          {error && <ErrorText>Something went wrong, we could not find the documents you were looking for.</ErrorText>}
          {!error && <InferenceList inferenceList={inferenceList} onItemClickUrl={onItemClickUrl} />}
        </Panel>
      </Collapse>
    </>
  );
};
